import React, { useState, useEffect, useContext } from "react";
import "./TeacherFeedbackEditor.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faRobot } from "@fortawesome/free-solid-svg-icons";
import formatFeedback from "./helpers/formatFeedback";
import { avatarImages } from "../../profilepage/AvatarConfig";
import { UserContext } from "../../../../App";

const ModuleOverallFeedback = ({
  feedback,
  onSave,
  isEditing,
  onEditClick,
  onAIFeedbackRequest,
  aiFeedback,
  isTeacher,
  studentResponses,
}) => {
  const [editedFeedback, setEditedFeedback] = useState(feedback || "");
  const [combinedFeedback, setCombinedFeedback] = useState(feedback || "");
  const [markedForDeletion, setMarkedForDeletion] = useState(false);
  const [feedbackDeleted, setFeedbackDeleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { role } = useContext(UserContext);

  const moduleOverallId = "__MODULE_OVERALL__";

  // Only allow AI feedback for plus roles and admin
  const canUseAIFeedback = ["teacherplus", "admin", "technicianplus"].includes(
    role
  );

  useEffect(() => {
    if (feedbackDeleted) {
      return;
    }
    if (aiFeedback && !combinedFeedback) {
      const processedFeedback = formatFeedback.processAIFeedback(aiFeedback);
      setCombinedFeedback(processedFeedback);
      setEditedFeedback(processedFeedback);
      setMarkedForDeletion(false);
      onSave(moduleOverallId, processedFeedback);
    }
  }, [aiFeedback, onSave, combinedFeedback, feedbackDeleted, moduleOverallId]);

  useEffect(() => {
    if (aiFeedback) {
      setFeedbackDeleted(false);
    }
  }, [aiFeedback]);

  const handleLocalSave = () => {
    const trimmedFeedback = editedFeedback.trim();
    onSave(moduleOverallId, trimmedFeedback || null);
    setCombinedFeedback(trimmedFeedback || null);
    onEditClick(null);
    setFeedbackDeleted(false);
  };

  const handleAIFeedbackClick = async () => {
    setIsLoading(true);
    await onAIFeedbackRequest(moduleOverallId);
    setIsLoading(false);
  };

  const handleDeleteClick = () => {
    setMarkedForDeletion(!markedForDeletion);
    if (!markedForDeletion) {
      onSave(moduleOverallId, "__DELETE__");
      setCombinedFeedback(null);
      setEditedFeedback("");
      setFeedbackDeleted(true);
    }
  };

  const renderFeedbackMeta = () => {
    const grade = studentResponses?.[moduleOverallId]?.grade;
    if (!grade?.gradedAt || !grade?.graderName) return null;

    let timestamp;
    if (grade.gradedAt.seconds) {
      // Handle Firestore timestamp
      timestamp = grade.gradedAt.seconds * 1000;
    } else if (grade.gradedAt instanceof Date) {
      // Handle JavaScript Date object
      timestamp = grade.gradedAt.getTime();
    } else if (grade.gradedAt._seconds) {
      // Handle Firestore timestamp from fresh fetch
      timestamp = grade.gradedAt._seconds * 1000;
    } else {
      // Handle direct timestamp
      timestamp = grade.gradedAt;
    }

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      console.error("Invalid date:", grade.gradedAt);
      return null;
    }

    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <div className="feedback-meta">
        <small>
          <strong>{grade.graderName}</strong> on {formattedDate}
        </small>
      </div>
    );
  };

  // Student view
  if (!isTeacher) {
    return feedback ? (
      <div className="teacher-feedback">
        <div className="feedback-header">
          <h4>Overall Module Feedback:</h4>
        </div>
        <div className="feedback-content-wrapper">
          {studentResponses?.[moduleOverallId]?.grade?.graderAvatar && (
            <img
              src={
                avatarImages[
                  studentResponses[moduleOverallId].grade.graderAvatar
                ]
              }
              alt="Teacher Avatar"
              className="feedback-avatar"
            />
          )}
          <div className="feedback-content">
            {renderFeedbackMeta()}
            <p
              dangerouslySetInnerHTML={{
                __html: formatFeedback.formatForDisplay(feedback),
              }}
            />
          </div>
        </div>
      </div>
    ) : null;
  }

  // Teacher view - editing mode
  if (isEditing) {
    return (
      <div className="teacher-feedback-editor">
        <textarea
          value={editedFeedback}
          onChange={(e) => setEditedFeedback(e.target.value)}
          placeholder="Add your overall module feedback here..."
        />
        <div className="editor-buttons">
          <button onClick={handleLocalSave} className="common-button">
            Update Feedback
          </button>
          <button
            onClick={() => onEditClick(null)}
            className="control-button cancel-button"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Teacher view - display mode
  return (
    <div className="teacher-feedback">
      <div className="feedback-header">
        <h4>Overall Module Feedback:</h4>
        <div className="feedback-controls">
          {canUseAIFeedback && (
            <button
              onClick={handleAIFeedbackClick}
              className="feedback-control-button"
              disabled={isLoading}
            >
              <FontAwesomeIcon
                icon={faRobot}
                className="ai-icon"
                spin={isLoading}
              />
              {isLoading ? "Generating Summary..." : "Generate AI Summary"}
            </button>
          )}
          <button
            onClick={() => onEditClick(moduleOverallId)}
            className="feedback-control-button"
          >
            {combinedFeedback ? "Edit Feedback" : "Add Feedback"}
          </button>
          {combinedFeedback && (
            <button
              onClick={handleDeleteClick}
              className={`feedback-control-button delete-feedback-button ${
                markedForDeletion ? "active" : ""
              }`}
            >
              <FontAwesomeIcon icon={faTrash} className="delete-icon" />
              Delete Feedback
            </button>
          )}
        </div>
      </div>
      <div className="feedback-content-wrapper">
        {studentResponses?.[moduleOverallId]?.grade?.graderAvatar && (
          <img
            src={
              avatarImages[studentResponses[moduleOverallId].grade.graderAvatar]
            }
            alt="Teacher Avatar"
            className="feedback-avatar"
          />
        )}
        <div className="feedback-content">
          {renderFeedbackMeta()}
          {combinedFeedback ? (
            <p
              className={markedForDeletion ? "marked-for-deletion" : ""}
              dangerouslySetInnerHTML={{
                __html: formatFeedback.formatForDisplay(combinedFeedback),
              }}
            />
          ) : (
            <p className="no-feedback">
              No overall module feedback provided yet
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModuleOverallFeedback;
